import React, { useContext, useEffect, useState } from 'react';
import {
  AppBar,
  Avatar,
  Box,
  Divider,
  IconButton,
  InputBase,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Paper,
  Toolbar,
  Typography,
  ListItemButton,
  CircularProgress,
} from '@mui/material';
import { Search } from '@mui/icons-material';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import { useNavigate } from 'react-router-dom';
import FormsBottomNavbar from '../Buy Module/CustomNav'; // adjust path
import { AuthContext } from './../../AuthContext/AuthContext';
import axios from 'axios';
import { BASE_URL } from './../../Api/ApiUrls';

const API_BASE = 'https://landnest.net:7000/api';

const AuctionInbox = () => {
  const navigate = useNavigate();
  const { userId } = useContext(AuthContext);

  const [messages, setMessages] = useState([]);
  const [filteredMessages, setFilteredMessages] = useState([]);
  const [searchText, setSearchText] = useState('');
  const [users, setUsers] = useState({});
  const [properties, setProperties] = useState({});
  const [loading, setLoading] = useState(true);

  const colors = {
    primary: '#1a1a1a',
    background: '#efe7dd',
    cardBg: '#ffffff',
    muted: '#666',
  };

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        // ── Auction properties ──────────────────────────────────────────────
        let propertiesArray = [];
        try {
          const auctionRes = await axios.get(
            `${API_BASE}/properties/jvjd/auction/non-admin/?limit=500`
          );
          propertiesArray = Array.isArray(auctionRes.data?.properties)
            ? auctionRes.data.properties
            : Array.isArray(auctionRes.data?.data)
              ? auctionRes.data.data
              : Array.isArray(auctionRes.data)
                ? auctionRes.data
                : [];
        } catch {
          const propRes = await axios.get(`${BASE_URL}/property/`);
          propertiesArray = propRes.data?.data || propRes.data || [];
        }

        const propertiesData = propertiesArray.reduce((acc, property) => {
          const pid = property.id ?? property.property_id ?? property.req_id;
          if (pid != null) {
            acc[String(pid)] = {
              ...property,
              property_id: pid,
              property_name:
                property.propertyType ||
                property.property_type ||
                property.property_name ||
                property.bankName ||
                `Auction ${pid}`,
              user_id: property.userId ?? property.user_id,
              bankName: property.bankName || property.bank_name || '',
            };
          }
          return acc;
        }, {});
        setProperties(propertiesData);

        // ── Messages ────────────────────────────────────────────────────────
        const messagesResponse = await axios.get(`${BASE_URL}/chat-messages/`);
        const messagesData = (messagesResponse.data || []).filter(
          (msg) => msg.property_id != null
        );

        // ── Users ───────────────────────────────────────────────────────────
        const usersResponse = await axios.get(`${BASE_URL}/users/`);
        const usersArray = usersResponse.data?.data || usersResponse.data || [];
        const usersData = usersArray.reduce((acc, user) => {
          acc[user.user_id] = user;
          return acc;
        }, {});
        setUsers(usersData);

        // Current user only
        const userMessages = messagesData.filter(
          (msg) =>
            String(msg.user_id) === String(userId) ||
            String(msg.receiver) === String(userId)
        );

        // Only chats for known auction properties
        const auctionIds = new Set(Object.keys(propertiesData));
        const auctionUserMessages = userMessages.filter((msg) =>
          auctionIds.has(String(msg.property_id))
        );

        // Latest per property
        const latestMessagesMap = new Map();
        auctionUserMessages.forEach((msg) => {
          const key = String(msg.property_id);
          if (!latestMessagesMap.has(key)) {
            latestMessagesMap.set(key, msg);
          }
        });

        const latestMessages = Array.from(latestMessagesMap.values());
        setMessages(latestMessages);
        setFilteredMessages(latestMessages);
      } catch (error) {
        console.error('Failed to fetch Auction inbox data:', error);
      } finally {
        setLoading(false);
      }
    };

    if (userId) fetchData();
  }, [userId]);

  useEffect(() => {
    if (loading) return;
    const lowerSearch = searchText.toLowerCase();
    const filtered = messages.filter((msg) => {
      const prop = properties[String(msg.property_id)] || {};
      const name = (prop.property_name || '').toLowerCase();
      const bank = (prop.bankName || '').toLowerCase();
      return (
        (msg.message || '').toLowerCase().includes(lowerSearch) ||
        name.includes(lowerSearch) ||
        bank.includes(lowerSearch) ||
        String(msg.property_id || '').includes(lowerSearch)
      );
    });
    setFilteredMessages(filtered);
  }, [searchText, messages, properties, loading]);

  const getOtherUser = (message) => {
    const otherUserId =
      String(message.user_id) === String(userId)
        ? message.receiver
        : message.user_id;
    return users[otherUserId] || {};
  };

  if (loading) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 2,
          backgroundColor: colors.background,
        }}
      >
        <CircularProgress sx={{ color: '#000' }} />
        <Typography fontWeight={600}>Loading Auction Inbox...</Typography>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        width: '100%',
        maxWidth: 800,
        mx: 'auto',
        minHeight: '100vh',
        bgcolor: 'rgb(239, 231, 221)',
        borderRadius: { xs: 0, sm: 3 },
        boxShadow: 3,
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <AppBar position="static" elevation={0} sx={{ bgcolor: 'black', color: 'white' }}>
        <Toolbar sx={{ justifyContent: 'space-between' }}>
          <IconButton
            edge="start"
            size="small"
            onClick={() => navigate(-1)}
            sx={{ color: 'white' }}
          >
            <ArrowBackIosNewIcon />
          </IconButton>
          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
            Auction Inbox ({filteredMessages.length})
          </Typography>
          <IconButton edge="end" size="small" sx={{ color: 'white' }} />
        </Toolbar>
      </AppBar>

      <Paper
        sx={{
          m: 2,
          px: 2,
          py: 0.5,
          display: 'flex',
          alignItems: 'center',
          borderRadius: 5,
          backgroundColor: '#f0f0f0',
          border: '1px solid black',
        }}
      >
        <Search sx={{ color: 'gray', mr: 1 }} />
        <InputBase
          placeholder="Search auction chats"
          fullWidth
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
        />
      </Paper>

      <Box sx={{ flexGrow: 1, overflowY: 'auto', px: 1, pb: 7 }}>
        <List>
          {filteredMessages.length > 0 ? (
            filteredMessages.map((msg, index) => {
              const property = properties[String(msg.property_id)] || {};
              const otherUser = getOtherUser(msg);
              const isPropertyOwner =
                String(property.user_id) === String(userId);

              return (
                <React.Fragment key={`${msg.property_id}-${index}`}>
                  <ListItemButton
                    alignItems="flex-start"
                    onClick={() =>
                      navigate(
                        `/chat/${msg.property_id}/${otherUser.user_id || msg.receiver || msg.user_id}`
                      )
                    }
                  >
                    <ListItemAvatar>
                      <Avatar src={otherUser.profile}>
                        {otherUser.first_name?.[0] || 'U'}
                      </Avatar>
                    </ListItemAvatar>
                    <ListItemText
                      primary={
                        <Box
                          sx={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            gap: 1,
                          }}
                        >
                          <Typography fontWeight={500} noWrap>
                            {property.property_name ||
                              `Auction ${msg.property_id}`}
                            {property.bankName ? ` · ${property.bankName}` : ''}
                            {isPropertyOwner && ' (Your Property)'}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {msg.created_at
                              ? new Date(msg.created_at).toLocaleDateString()
                              : ''}
                          </Typography>
                        </Box>
                      }
                      secondary={
                        <Typography variant="body2" noWrap>
                          {msg.message}
                        </Typography>
                      }
                    />
                  </ListItemButton>

                  {index !== filteredMessages.length - 1 && (
                    <Divider variant="inset" component="li" />
                  )}
                </React.Fragment>
              );
            })
          ) : (
            <ListItem>
              <ListItemText
                primary="No auction messages found"
                secondary="You don't have any auction conversations yet"
                sx={{ textAlign: 'center' }}
              />
            </ListItem>
          )}
        </List>
      </Box>

      <Box sx={{ position: 'fixed', bottom: 0, left: 0, right: 0 }}>
        <FormsBottomNavbar />
      </Box>
    </Box>
  );
};

export default AuctionInbox;